import { DateRangePicker } from '@/components/common/DateRangePicker';
import { Modal } from '@/components/common/Modal';
import Pagination from '@/components/common/Pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ResponsiveDataTable from '@/components/mobileCards/ResponsiveDataTable';
import useAccessControl from '@/hooks/RolesAndPriviledges/useAccessControl';
import DeleteModal from '@/pages/Banker-management/Modal/DeleteModal';
import { User } from '@/types/rolesTypes';
import { formatReadableDateTime, getErrorMessage, showToastMessage } from '@/utils';
import { ColumnDef } from '@tanstack/react-table';
import { Search, X } from 'lucide-react';
import { useState } from 'react';
import CreateAccessControl from '../components/CreateAccessControl';
import { useSensitiveData } from '@/hooks/useSensitiveData';
import { usePermissions } from '@/context/PermissionContext';
import { dotColor, statusColor } from '@/utils/constent';
import ChangePassword from '@/pages/Downline/Modal/PasswordChange';
import { FieldName } from '@/types';
import { useNavigate } from 'react-router-dom';
import { useChangeRoleUserStatusMutation } from '@/store/api/rolesApi';

export type SelectedUser = {
  id: string;
  username: string;
  fieldName: FieldName;
  value?: string;
};
const AccessControl = () => {
  const {
    accessData,
    isLoading,
    selectedRange,
    setSelectedRange,
    setAppliedRange,
    setCurrentPage,
    totalItems,
    pageSize,
    setPageSize,
    currentPage,
    setSearch,
    search,
    handleDeleteRole,
    activeModal,
    setActiveModal,
    deleteId,
    setDeleteId,
    closeModal,
    sorting,
    handleSortingChange,
  } = useAccessControl();
  const { hasModuleAccess } = usePermissions();
  const navigate = useNavigate();
  const canUpdate = hasModuleAccess('Roles-Privileges', 'update');
  const canDelete = hasModuleAccess('Roles-Privileges', 'delete');
  const canSeeDetailedView = hasModuleAccess('Roles-Privileges', 'detailedView');
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [openChangePass, setOpenChangePass] = useState(false);

  const { revealEmail, revealPhone } = useSensitiveData();
  const [info, setInfo] = useState({ id: 0, type: '' });
  const onCancel = () => setOpenModal(false);
  const [changeStatus, { isLoading: statusUpdating }] = useChangeRoleUserStatusMutation();

  const handleSubmit = async (id: number, newStatus: 'Active' | 'Inactive') => {
    if (!id) return;

    try {
      await changeStatus({
        userId: id,
        status: newStatus,
      }).unwrap();
      showToastMessage('success', 'Change Status Successfully!');
      onCancel();
    } catch (error) {
      showToastMessage('error', getErrorMessage(error));
    }
  };
  const closeModalChangePass = () => setOpenChangePass(false);

  const administratorsColumns: ColumnDef<User>[] = [
    {
      accessorKey: 'sno',
      enableSorting: false,
      meta: {
        align: 'center',
      },
      header: 'S. No.',
      cell: ({ row }) => (
        <div className="text-center">{(currentPage - 1) * pageSize + row.index + 1}</div>
      ),
    },

    {
      accessorKey: 'username',
      header: 'User Name',
      cell: ({ row }) => <div className=" ">{row.original?.firstname}</div>,
    },

    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => <div className=" ">{row.original?.email || ''}</div>,
    },
    {
      accessorKey: 'name',
      header: 'Role Name',
      cell: ({ row }) => <div className=" ">{row.original?.role?.name}</div>,
    },
    {
      // accessorKey: 'description',
      header: 'Description',
      // accessorFn: (row) => {
      //   row?.role?.description;
      // }, // ✅ gives sorter a real Date object
      sortingFn: 'text',
      cell: ({ row }) => <div className="">{row.original?.role?.description}</div>,
    },
    {
      accessorKey: 'permission',
      enableSorting: false,
      header: 'Permission',
      meta: {
        align: 'center',
      },
      cell: ({ row }) => {
        const roleId = row.original.role.id;
        return (
          <div className=" flex justify-center items-center">
            <Button
              disabled={!canSeeDetailedView}
              onClick={() => navigate(`/create-roles/${roleId}/view`)}
            >
              View
            </Button>
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      meta: {
        align: 'center',
      },
      cell: ({ row }) => (
        <div className="text-center">{formatReadableDateTime(row.original.createdAt)}</div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const user = row.original;
        const statusKey = user.status?.toLowerCase() as keyof typeof statusColor;
        return (
          <div
            className={`flex items-center gap-2 px-2 py-1 w-fit  rounded-full  ${statusColor[statusKey]}`}
          >
            <div className={`w-2 h-2 rounded-full ${dotColor[statusKey]}`}></div>
            {user.status}
          </div>
        );
      },
    },
    {
      id: 'action',
      header: 'Action',
      enableSorting: false,
      meta: {
        align: 'center',
      },
      cell: ({ row }) => {
        const { id } = row.original;
        const isActive = row.original.status === 'Active';

        return (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              className="text-black"
              disabled={!canUpdate}
              onClick={() => {
                setOpenModal(true);
                setInfo({
                  id,
                  type: 'edit',
                });
              }}
            >
              Edit
            </Button>

            <Button
              disabled={!canUpdate || statusUpdating}
              variant="outline"
              className={`w-[88px] border-none ${
                isActive
                  ? 'bg-red-100 text-red-600 hover:bg-red-200'
                  : 'bg-light text-dark-green hover:bg-light/80'
              }`}
              onClick={() => {
                const newStatus = isActive ? 'Inactive' : 'Active';
                handleSubmit(Number(id), newStatus);
              }}
            >
              {isActive ? 'Inactive' : 'Active'}
            </Button>
            <Button
              variant="yellow"
              className="text-white"
              disabled={!canUpdate}
              onClick={() => {
                if (canUpdate) {
                  setOpenChangePass(true);
                  setSelectedUser({
                    id: String(id) ?? '',
                    username: row.original?.username ?? '',
                    fieldName: row.original.firstname as FieldName,
                    value: '******',
                  });
                }
              }}
            >
              Change Password
            </Button>
            <Button
              disabled={!canDelete}
              onClick={() => {
                setDeleteId(id);
                setActiveModal('deleteModal');
              }}
              variant="destructive"
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder={`Search by username...`}
            className=" pl-9 text-[#162f34] border-green focus-visible:ring-green"
            value={search}
            onChange={(e) => {
              setSearch?.(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <DateRangePicker
          value={selectedRange}
          onChange={(newRange) => setSelectedRange(newRange)}
          onApply={() => {
            setAppliedRange(selectedRange);
            setCurrentPage(1);
          }}
          className="w-fit"
        />
      </div>
      <div className="py-2">
        <ResponsiveDataTable
          data={accessData ?? []}
          columns={administratorsColumns}
          isLoading={isLoading}
          variant="default"
          pinnedKeys={['username', 'status']}
          actionKeys={['permission', 'action']}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
        />
      </div>
      <Pagination
        currentPage={currentPage}
        totalItems={totalItems || 0}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        pageSizeOptions={[10, 25, 50, 100, 500, 1000]}
      />
      {activeModal && (
        <Modal
          open={!!activeModal}
          onOpenChange={closeModal}
          title={'Delete Role'}
          closeBtn={<X />}
          style={activeModal === 'qr' && 'max-w-[70dvw]'}
        >
          {activeModal === 'deleteModal' && (
            <DeleteModal
              onCancel={closeModal}
              deleteId={String(deleteId)}
              onConfirm={handleDeleteRole}
              phrase="this Access"
            />
          )}
        </Modal>
      )}
      <Modal
        open={openModal}
        onOpenChange={() => setOpenModal(false)}
        title={'Manage Roles'}
        closeBtn={<X />}
        style="overflow-visible"
        // fullWidth={activeTab === 'Notification'}
      >
        {openModal && <CreateAccessControl onCancel={onCancel} id={info.id} type={info.type} />}
      </Modal>

      <Modal
        open={!!openChangePass}
        onOpenChange={closeModalChangePass}
        title={openChangePass ? 'Change Password' : ''}
        closeBtn={<X />}
      >
        {openChangePass && selectedUser && (
          <ChangePassword
            selectedUser={selectedUser}
            onCancel={closeModalChangePass}
            isRoleUser={true}
          />
        )}
      </Modal>
    </>
  );
};

export default AccessControl;
